import { ChangeDetectorRef, DestroyRef, Directive, inject } from '@angular/core';
import { takeUntilDestroyed } from '@angular/core/rxjs-interop';
import { FormControl } from '@angular/forms';
import { combineLatest } from 'rxjs';
import { distinctUntilChanged, tap } from 'rxjs/operators';

import { BudgetService } from '@features/budget/services/budget.service';
import { SnapshotService } from '@features/budget/services/snapshot.service';

import { TrialInsightsStore } from '@models/trial-insights/trial-insights.store';

import { EventService } from '@shared/store/event/event.service';
import { MainQuery } from '@shared/store/main/main.query';
import { OrganizationQuery } from '@shared/store/organization/organization.query';
import { OrganizationService } from '@shared/store/organization/organization.service';
import { OrganizationStore } from '@shared/store/organization/organization.store';
import { PeriodType } from '@shared/utils/utils';

import {
  BudgetCompareMenuFlatten$,
  BudgetCompareMenuPeriodFormControl,
  BudgetCompareMenuPeriodList,
  BudgetCompareMenuSnapshotChangeFn,
  BudgetCompareMenuSnapshotDisabled,
  BudgetCompareMenuSnapshotFormControl,
  BudgetCompareMenuSnapshotList,
  BudgetCompareMenuSnapshotLoading,
  BudgetCompareMenuSnapshotRefreshFn,
  BudgetCompareMenuSnapshotShowDelete,
  BudgetCompareMenuSnapshotShowEdit,
  BudgetCompareMenuSnapshotValue,
  BudgetCompareMenuVendorChangeFn,
  BudgetCompareMenuVendorFormControl,
  BudgetCompareMenuVendorLoading,
  defaultPeriodList,
  defaultSnapshotRefreshFn,
} from '../models/budget-compare-menu.models';

export interface ComponentConfig {
  budgetService: BudgetService;
  cdr: ChangeDetectorRef;
  eventService: EventService;
  mainQuery: MainQuery;
  organizationQuery: OrganizationQuery;
  organizationService: OrganizationService;
  organizationStore: OrganizationStore;
  snapshotService: SnapshotService;
  trialInsightsStore: TrialInsightsStore;
}

@Directive()
export class GenericBudgetCompareMenuComponent {
  private readonly destroyRef = inject(DestroyRef);

  budgetService: BudgetService;
  cdr: ChangeDetectorRef;
  eventService: EventService;
  initializeVendorsComplete: boolean;
  mainQuery: MainQuery;
  organizationQuery: OrganizationQuery;
  organizationService: OrganizationService;
  organizationStore: OrganizationStore;
  periodFormControl: BudgetCompareMenuPeriodFormControl;
  periodList: BudgetCompareMenuPeriodList;
  snapshotDisabled: BudgetCompareMenuSnapshotDisabled;
  snapshotFormControl: BudgetCompareMenuSnapshotFormControl;
  snapshotList: BudgetCompareMenuSnapshotList;
  snapshotLoading: BudgetCompareMenuSnapshotLoading;
  snapshotRefreshFn: BudgetCompareMenuSnapshotRefreshFn = defaultSnapshotRefreshFn;
  snapshotService: SnapshotService;
  snapshotShowDelete: BudgetCompareMenuSnapshotShowDelete;
  snapshotShowEdit: BudgetCompareMenuSnapshotShowEdit;
  snapshotValue?: BudgetCompareMenuSnapshotValue;
  trialInsightsStore: TrialInsightsStore;
  vendorFormControl: BudgetCompareMenuVendorFormControl;
  vendorList: BudgetCompareMenuFlatten$<typeof this.organizationQuery.allVendors$>;
  vendorLoading: BudgetCompareMenuVendorLoading;

  // eslint-disable-next-line @angular-eslint/prefer-inject
  constructor(config: ComponentConfig) {
    this.cdr = config.cdr;
    this.mainQuery = config.mainQuery;
    this.eventService = config.eventService;
    this.organizationService = config.organizationService;
    this.organizationStore = config.organizationStore;
    this.organizationQuery = config.organizationQuery;
    this.snapshotService = config.snapshotService;
    this.budgetService = config.budgetService;
    this.trialInsightsStore = config.trialInsightsStore;

    this.vendorFormControl = new FormControl<string>('', {
      nonNullable: true,
    });
    this.vendorList = [];
    this.vendorLoading = false;

    this.snapshotFormControl = new FormControl<string>('');
    this.snapshotList = [];
    this.snapshotValue = '';
    this.snapshotDisabled = false;
    this.snapshotShowEdit = false;
    this.snapshotShowDelete = false;
    this.snapshotLoading = false;

    this.periodFormControl = new FormControl<string>(PeriodType.PERIOD_MONTH, {
      nonNullable: true,
    });
    this.periodList = defaultPeriodList;

    this.initializeVendorsComplete = false;
  }

  initializeSubscriptions(): void {
    this.subscribeToVendorList();
    this.subscribeToVendorListLoading();
    this.subscribeToVendorListFormControl();
    this.subscribeToVendorListRequest();

    this.subscribeToSnapshotList();
    this.subscribeToSnapshotListLoading();
    this.subscribeToSnapshotListFormControl();
    this.subscribeToSnapshotListRequest();
  }

  initializeVendors(vendorList: typeof this.vendorList): void {
    if (!vendorList || !vendorList.length || this.initializeVendorsComplete) {
      return;
    }

    if (vendorList.length === 1) {
      this.organizationStore.setActive(vendorList[0].id);
      this.vendorFormControl.setValue(vendorList[0].id);
    } else {
      this.organizationStore.setActive(null);
      this.vendorFormControl.setValue('');
    }

    this.initializeVendorsComplete = true;
  }

  snapshotChangeFn: BudgetCompareMenuSnapshotChangeFn = (value: string) => {
    this.snapshotValue = value;
  };

  snapshotResetFn(): void {
    this.snapshotValue = '';
  }

  subscribeToSnapshotList(): void {
    this.snapshotService
      .getSnapShotVersions()
      .pipe(takeUntilDestroyed(this.destroyRef))
      .subscribe((snapshotList) => {
        this.snapshotList = snapshotList;
        this.cdr.markForCheck();
      });
  }

  subscribeToSnapshotListFormControl(): void {
    this.snapshotFormControl.valueChanges
      .pipe(
        distinctUntilChanged(),
        tap((snapshotName) => {
          this.trialInsightsStore.update((state) => {
            return {
              ...state,
              bvaChart: {
                ...state.bvaChart,
                snapshotName,
              },
            };
          });
        }),
        // To-do: Resolve getBudgetSnapshots issues

        // switchMap((snapshotName) => {
        //   this.snapshotChangeFn(snapshotName || '');

        //   if (!snapshotName) {
        //     this.snapshotService.setOriginalBudgetData();
        //     return of();
        //   }

        //   return this.snapshotService.getBudgetSnapshots(snapshotName);
        // }),
        tap(() => this.cdr.markForCheck())
      )
      .pipe(takeUntilDestroyed(this.destroyRef))
      .subscribe();
  }

  subscribeToSnapshotListLoading(): void {
    this.snapshotService.loading$
      .pipe(takeUntilDestroyed(this.destroyRef))
      .subscribe((snapshotLoading) => {
        this.snapshotLoading = snapshotLoading;
        this.cdr.markForCheck();
      });
  }

  subscribeToSnapshotListRequest(): void {
    combineLatest([this.mainQuery.select('trialKey')])
      .pipe(
        tap(() => this.snapshotResetFn()),
        this.snapshotService.getSnapshotListPipeline()
      )
      .pipe(takeUntilDestroyed(this.destroyRef))
      .subscribe();
  }

  subscribeToVendorList(): void {
    this.organizationQuery.allVendors$
      .pipe(takeUntilDestroyed(this.destroyRef))
      .subscribe((vendorList) => {
        this.vendorList = vendorList;
        this.initializeVendors(vendorList);
        this.cdr.markForCheck();
      });
  }

  subscribeToVendorListFormControl(): void {
    this.vendorFormControl.valueChanges
      .pipe(distinctUntilChanged())
      .pipe(takeUntilDestroyed(this.destroyRef))
      .subscribe((vendorId) => {
        this.trialInsightsStore.update((state) => {
          return {
            ...state,
            bvaChart: {
              ...state.bvaChart,
              vendorId,
            },
          };
        });
      });
  }

  subscribeToVendorListLoading(): void {
    this.organizationQuery
      .selectLoading()
      .pipe(takeUntilDestroyed(this.destroyRef))
      .subscribe((vendorLoading) => {
        this.vendorLoading = vendorLoading;
        this.cdr.markForCheck();
      });
  }

  subscribeToVendorListRequest(): void {
    combineLatest([this.mainQuery.select('trialKey')])
      .pipe(
        tap(() => this.vendorResetFn()),
        this.organizationService.getListWithTotalBudgetAmountPipeline()
      )
      .pipe(takeUntilDestroyed(this.destroyRef))
      .subscribe();
  }

  vendorChangeFn: BudgetCompareMenuVendorChangeFn = (vendorId: string) => {
    this.organizationStore.setActive(vendorId || null);
  };

  vendorResetFn(): void {
    this.initializeVendorsComplete = false;
  }
}
